import React from 'react';
import { motion } from 'motion/react';

import './ZoneTheme.css';

// Shared building blocks for the "Zone" tabs — the Overview 7 neo-brutalist
// look applied to Live / Sold / Traded / Wishlist / Investments / Analytics.
// Keeping these in one place is what makes the six tabs feel like one system.

export const CANDY = ['#ffd43d', '#ff9bd2', '#6fe8cf', '#b79cff', '#c9f65a', '#ffab5e', '#fffef8'];

export const ZONE = {
  black:  '#131313',
  white:  '#fffef8',
  yellow: '#ffd43d',
  pink:   '#ff9bd2',
  hot:    '#ff5c8a',
  teal:   '#6fe8cf',
  lime:   '#c9f65a',
  purple: '#b79cff',
  orange: '#ffab5e',
  green:  '#3ddc84',
  win:    '#0d7a43',
  lose:   '#c8134b',
  mute:   'rgba(19,19,19,0.55)',
};

export const COND_ABBR = {
  'Factory New': 'FN', 'Minimal Wear': 'MW', 'Field Tested': 'FT',
  'Well Worn': 'WW', 'Battle Scarred': 'BS',
};

export function getItemTypes(row) {
  if (!row?.itemType) return [];
  return Array.isArray(row.itemType) ? row.itemType : [row.itemType];
}

/** Category colours are user-chosen, so a solid fill can be pale or near-black.
 *  Returns the ink colour that stays readable on top of it. */
export function readableInk(hex) {
  let h = String(hex || '').replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return ZONE.black;
  const n = parseInt(h, 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(v => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) > 0.42 ? ZONE.black : ZONE.white;
}

// findSkinImage is the shared O(1) lookup from skinIndex.js, re-exported
// here so the pages importing it from this module stay unchanged.
export { findSkinImage } from './skinIndex';

/** 1234.5 → "1,234.50"  ·  12.5 → "12.50" (always absolute, always cents).
 *  Cents are never dropped: the classic tabs print toFixed(2) everywhere, so
 *  rounding here would make the same row read differently per theme. */
export const fmt = (n) => Math.abs(Number(n) || 0)
  .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** -12.5 → "-$12.50" */
export const signed = (n) => ((Number(n) || 0) >= 0 ? '+$' : '-$') + fmt(n);

export const pct = (n) => `${(Number(n) || 0) >= 0 ? '+' : '-'}${Math.abs(Number(n) || 0).toFixed(1)}%`;

export const pnlColor = (n) => (Number(n) || 0) >= 0 ? ZONE.win : ZONE.lose;

/** Cards drop in from above with a slight tilt, like pinned paper. */
export const drop = (i = 0, rot = 0) => ({
  initial: { opacity: 0, y: -22, rotate: rot + (i % 2 ? 2.5 : -2.5) },
  animate: { opacity: 1, y: 0, rotate: rot },
  transition: { type: 'spring', stiffness: 320, damping: 24, delay: Math.min(i, 16) * 0.05 },
});

/** Grid tiles fade up in sequence. */
export const rise = (i = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.34, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 20) * 0.03 },
});

/* ─── Header ──────────────────────────────────────────────── */
export function ZoneHead({ pre, hl, post, sub, stickers = [], children }) {
  return (
    <div className="zn-head">
      <div style={{ minWidth: 0 }}>
        <motion.h1 className="zn-title" {...drop(0)}>
          {pre}{pre ? ' ' : ''}<span className="hl">{hl}</span>{post ? ` ${post}` : ''}
        </motion.h1>
        {sub && <p className="zn-sub">{sub}</p>}
      </div>
      {stickers.filter(Boolean).map((s, i) => (
        <motion.span key={i} className="zn-sticker"
          style={{ background: s.bg || ZONE.white, transform: `rotate(${s.rot ?? (i % 2 ? 1.6 : -1.6)}deg)` }}
          {...drop(i + 1, s.rot ?? (i % 2 ? 1.6 : -1.6))}>
          {s.text}
        </motion.span>
      ))}
      {children}
    </div>
  );
}

/* ─── Stat row ────────────────────────────────────────────── */
export function ZoneStats({ items = [] }) {
  return (
    <div className="zn-stats">
      {items.map((s, i) => {
        const rot = [-1.2, 0.8, -0.6, 1.1, -0.9][i % 5];
        const Icon = s.icon;
        return (
          <motion.div key={s.label} className="zn-stat"
            style={{ background: s.bg || ZONE.white, transform: `rotate(${rot}deg)` }}
            {...drop(i + 3, rot)}>
            <div className="zn-stat-top">
              <span className="zn-stat-label">{s.label}</span>
              {Icon && <span className="zn-stat-icon"><Icon size={15} strokeWidth={2.6} /></span>}
            </div>
            <div className="zn-stat-value" style={s.color ? { color: s.color } : undefined}>{s.value}</div>
            {s.sub && <div className="zn-stat-sub">{s.sub}</div>}
          </motion.div>
        );
      })}
    </div>
  );
}

/* ─── Toolbar bits ────────────────────────────────────────── */
export function ZoneSearch({ value, onChange, placeholder }) {
  return (
    <div className="zn-search">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5">
        <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" />
      </svg>
      <input placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

export function ZoneChips({ options, value, onChange, allowToggleOff = false }) {
  return options.map(o => (
    <button key={o.key} type="button"
      className={`zn-chip ${value === o.key ? 'on' : ''}`}
      style={value === o.key && o.color ? { background: o.color } : undefined}
      onClick={() => onChange(allowToggleOff && value === o.key ? 'all' : o.key)}>
      {o.label}
    </button>
  ));
}

export function ZoneSelect({ value, onChange, options, label }) {
  return (
    <select className="zn-select" value={value} onChange={e => onChange(e.target.value)} aria-label={label}>
      {options.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  );
}

export function ZoneEmpty({ children }) {
  return <div className="zn-empty">{children}</div>;
}

/* ─── Footer strip ────────────────────────────────────────── */
export function ZoneFooter({ stickers = [] }) {
  return (
    <div className="zn-footer">
      {stickers.filter(Boolean).map((s, i) => (
        <span key={i} className="zn-sticker"
          style={{ background: s.bg || ZONE.white, transform: `rotate(${s.rot ?? (i % 2 ? 1.4 : -1.4)}deg)`, marginLeft: s.right ? 'auto' : undefined }}>
          {s.text}
        </span>
      ))}
    </div>
  );
}
